import { observer } from "mobx-react";
import { useMemo } from "react";
import { useRef, useEffect } from "react";
import { Chart } from "@antv/g2";
import { useStores } from "../../../../store";
import { formatNumber, getDecimalSeparator, __ } from "../../../../utils";
import { Col, Divider, Row, Statistic } from "antd";
function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
function getRandomData() {
  const days = 90;
  const startAtDay = 1;
  const data = [];
  for (let i = 1; i < days; i++) {
    const iPad = i.toString().padStart(2, "0");
    const sales = i >= startAtDay ? getRandomInt(1, 10) : 0;
    const clicks = i >= startAtDay ? getRandomInt(1, 1000) : 0;
    data.push({
      name: "sales",
      date: `2022- 01-${iPad}`,
      value: sales
    });
    data.push({
      name: "affiliateCouponUsages",
      date: `2022- 01-${iPad}`,
      value: sales > 3 ? getRandomInt(0, 2) : 0
    });
    data.push({
      name: "netCommissionValue",
      date: `2022- 01-${iPad}`,
      value: sales * getRandomInt(1, 4) * 10
    });
    data.push({
      name: "affiliateLinkClicks",
      date: `2022- 01-${iPad}`,
      value: clicks
    });
    data.push({
      name: "conversationRate",
      date: `2022- 01-${iPad}`,
      value: clicks > 0 ? getRandomInt(0, 6) : 0
    });
  }
  return data;
}
const COLORS = {
  darkgreen: "#1d9561",
  lightgreen: "#6cdfad",
  darkblue: "#4e99e5",
  lightblue: "#74afeb"
};
const PADDING = {
  left: 10,
  right: 10
};
const AccountAffiliateTransactionsChart = observer(() => {
  const ref = useRef();
  const {
    accountStore: {
      session: {
        affiliate: {
          statistics
        }
      }
    }
  } = useStores();
  const decimalSeparator = getDecimalSeparator();
  const summarizedData = useMemo(() => {
    const result = {
      sales: 0,
      affiliateCouponUsages: 0,
      netCommissionValue: 0,
      affiliateLinkClicks: 0,
      /**
       * The conversion rate is the quotient of sales per day and clicks per day. If the denominator
       * (clicks per day) is 0, then we have a mathematically unsolvable calculation problem (=> null).
       * Since null  can be represented in a two-dimensional graph, the denominator is always at least 1
       * and the quotient is max. 100%. If we add the standard assumption that with zero clicks per day
       * only zero sales per day can be achieved, we get a conversion rate of 0% on that day for a
       * two-dimensional graph.
       *
       * However, when calculating an average, if the denominator is 0, a conversion rate of 0% would be a
       * bias. Therefore, when calculating the average, you may only consider the days on which the denominator
       * of the average is > 0 and the result is not null.
       *
       * Practical: Filter out the conversion rate of all days on which there were 0 clicks before you calculate
       * the average of the conversion rate.
       *
       * @see https://app.clickup.com/2088/v/dc/218-533/218-9941?comment=90040001188609&threadedComment=90040001228661
       */
      avgConversionRate: 0,
      conversationRates: []
    };
    for (const {
      name,
      value
    } of statistics) {
      if (name === "conversationRate") {
        if (+value > 0) {
          result.conversationRates.push(+value);
          result.avgConversionRate += +value;
        }
      } else {
        result[name] += +value;
      }
    }
    result.avgConversionRate = result.avgConversionRate / (result.conversationRates.length || 1);
    return result;
  }, [statistics]);
  useEffect(() => {
    if (ref.current) {
      const data = process.env.REPLACE_ME ? getRandomData() : statistics;

      // Initiate chart
      const chart = new Chart({
        container: ref.current,
        autoFit: true,
        height: 500
      }).animate(false).tooltip({
        showCrosshairs: true
      }).legend(false).scale({
        date: {
          sync: true,
          range: [0, 1]
        },
        value: {
          sync: false
        }
      });
      chart.removeInteraction("tooltip");

      // Helper functions and constants
      const viewAnnotationText = (view, content) => {
        view.annotation().text({
          top: true,
          offsetY: -20,
          offsetX: 6,
          style: {
            fontSize: 14,
            fill: "#00000073"
          },
          position: ["min", "max"],
          content
        });
      };

      /**
       * ===============
       * Net commission
       * ===============
       */
      const view1 = chart.createView({
        region: {
          start: {
            x: 0,
            y: 0
          },
          end: {
            x: 1,
            y: 0.55
          }
        },
        padding: [25, PADDING.right, 60, PADDING.left]
      }).data(data.filter(_ref => {
        let {
          name
        } = _ref;
        return name === "netCommissionValue";
      }).map((_ref2, i, arr) => {
        let {
          date,
          name,
          value
        } = _ref2;
        return {
          index: i,
          date,
          name,
          value,
          cumulates: i === 0 ? value : +value + arr.slice(0, i).reduce((p, _ref3) => {
            let {
              value: c
            } = _ref3;
            return p + +c;
          }, 0)
        };
      })).tooltip({
        shared: true,
        showMarkers: true,
        customItems: items => [{
          ...items[0],
          color: COLORS.darkblue,
          name: __("Commissions"),
          value: `+ ${formatNumber(items[0].data.value, 2)} €`
        }, {
          ...items[0],
          color: COLORS.darkgreen,
          name: __("Commutated commissions"),
          value: `${formatNumber(items[0].data.cumulates, 2)} €`
        }]
      }).interaction("tooltip").interaction("sibling-tooltip").axis("date", false).axis("cumulates", {
        label: null
      }).axis("value", {
        line: null,
        tickLine: null,
        position: "right",
        top: true,
        grid: {
          line: null
        },
        label: null
      }).scale({
        cumulates: {},
        value: {
          range: [0, 0.3]
        }
      });
      view1.area().position("date*cumulates").color(`l(90) 0.8:${COLORS.darkgreen} 1.0:${COLORS.lightgreen}`);
      view1.line().position("date*cumulates").color(COLORS.darkgreen).style({
        lineWidth: 1
      });
      view1.line().position("date*value").color(COLORS.darkblue).shape("smooth");
      viewAnnotationText(view1, __("Net commissions growth"));

      /**
       * ===============
       * Sales
       * ===============
       */
      const view2 = chart.createView({
        region: {
          start: {
            x: 0,
            y: 0.55
          },
          end: {
            x: 1,
            y: 0.75
          }
        },
        padding: [0, PADDING.right, 60, PADDING.left]
      }).data(data).filter("name", v => ["sales", "affiliateCouponUsages"].indexOf(v) > -1).tooltip({
        shared: true,
        showMarkers: true,
        showTitle: false,
        customItems: items => {
          for (const item of items) {
            Object.assign(item, {
              name: item.name === "sales" ? __("Sales") : __("Coupon used")
            });
          }
          return items;
        }
      }).interaction("tooltip").interaction("sibling-tooltip").axis("date", {
        label: null
      }).axis("value", {
        label: null,
        grid: {
          line: null
        }
      });
      view2.interval().position("date*value").color("name", name => name === "sales" ? COLORS.lightblue : COLORS.darkblue);
      viewAnnotationText(view2, __("Sales"));

      /**
       * ===============
       * Conversion
       * ===============
       */
      const view3 = chart.createView({
        region: {
          start: {
            x: 0,
            y: 0.75
          },
          end: {
            x: 1,
            y: 1
          }
        },
        padding: [0, PADDING.right, 70, PADDING.left]
      }).interaction("tooltip").interaction("sibling-tooltip").data(data.filter(_ref4 => {
        let {
          name
        } = _ref4;
        return name === "affiliateLinkClicks";
      }).map(_ref5 => {
        var _data$filter$;
        let {
          value,
          name,
          date
        } = _ref5;
        return {
          value,
          name,
          date,
          conversationRate: (_data$filter$ = data.filter(_ref6 => {
            let {
              date: cDate,
              name
            } = _ref6;
            return cDate === date && name === "conversationRate";
          })[0]) === null || _data$filter$ === void 0 ? void 0 : _data$filter$.value
        };
      })).tooltip({
        shared: true,
        showMarkers: true,
        showTitle: false,
        customItems: items => {
          for (const item of items) {
            Object.assign(item, {
              name: item.name === "conversationRate" ? __("Conversion rate") : __("Link clicks"),
              value: item.name === "conversationRate" ? `${item.value} %` : item.value
            });
          }
          return items;
        }
      }).axis("date", {
        offsetY: 20
      } /* idk where to put this as the TS types are wrong but this works? */).axis("conversationRate", {
        label: null,
        grid: {
          line: null
        }
      }).axis("value", {
        line: null,
        label: null,
        tickLine: null,
        position: "right",
        grid: {
          line: null
        }
      }).scale("conversationRate", {
        max: 100
      });
      view3.line().position("date*conversationRate").color(COLORS.darkgreen).shape("smooth").style({
        lineWidth: 2
      });
      view3.line().position("date*value").shape("smooth").color(COLORS.darkblue).style({
        lineWidth: 1
      });
      viewAnnotationText(view3, __("Conversion rate and link clicks"));
      let timeout;
      chart.render();
      timeout = setTimeout(() => {
        // Sometimes, the chart does not render immediate, so we need to simulate a resize event
        window.dispatchEvent(new Event("resize"));
        timeout = setTimeout(() => {
          ref.current.style.opacity = "1";
        }, 500);
      }, 0);
      return () => {
        clearTimeout(timeout);
        chart.destroy();
      };
    }
    return () => {
      // Silence is golden.
    };
  }, [ref, statistics]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Row, {
    gutter: [20, 20],
    style: {
      padding: "0px 15px 15px"
    },
    align: "middle"
  }, /*#__PURE__*/React.createElement(Col, {
    flex: "1 1 auto"
  }, /*#__PURE__*/React.createElement(Statistic, {
    title: __("Net commissions"),
    suffix: "EUR",
    decimalSeparator: decimalSeparator,
    precision: 2,
    value: summarizedData.netCommissionValue
  })), /*#__PURE__*/React.createElement(Col, {
    style: {
      height: 30
    }
  }, /*#__PURE__*/React.createElement(Divider, {
    type: "vertical",
    style: {
      height: "100%",
      margin: 0
    }
  })), /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement(Statistic, {
    title: __("Sales"),
    precision: 0,
    value: summarizedData.sales
  })), /*#__PURE__*/React.createElement(Col, {
    style: {
      height: 30
    }
  }, /*#__PURE__*/React.createElement(Divider, {
    type: "vertical",
    style: {
      height: "100%",
      margin: 0
    }
  })), /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement(Statistic, {
    title: __("Coupon usages"),
    precision: 0,
    value: summarizedData.affiliateCouponUsages
  })), /*#__PURE__*/React.createElement(Col, {
    style: {
      height: 30
    }
  }, /*#__PURE__*/React.createElement(Divider, {
    type: "vertical",
    style: {
      height: "100%",
      margin: 0
    }
  })), /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement(Statistic, {
    title: __("Clicks on links"),
    precision: 0,
    value: summarizedData.affiliateLinkClicks
  })), /*#__PURE__*/React.createElement(Col, {
    style: {
      height: 30
    }
  }, /*#__PURE__*/React.createElement(Divider, {
    type: "vertical",
    style: {
      height: "100%",
      margin: 0
    }
  })), /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement(Statistic, {
    title: __("Avg. conversion rate"),
    prefix: "\xD8",
    suffix: "%",
    decimalSeparator: decimalSeparator,
    precision: 2,
    value: summarizedData.avgConversionRate
  }))), /*#__PURE__*/React.createElement(Divider, {
    style: {
      marginTop: 0
    }
  }), /*#__PURE__*/React.createElement("div", {
    ref: ref,
    style: {
      opacity: 0,
      transition: "opacity 250ms"
    }
  }));
});
export { AccountAffiliateTransactionsChart };